import { JSX, RenderableProps } from 'preact';
import { useCallback, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useAnimation, UseAnimationConfig } from '../hooks/UNSAFE_useAnimation';
import { useMessagesContext } from '../hooks/UNSAFE_useMessagesContext';
import { Transition, TransitionProps } from '../PRIVATE_TransitionGroup';

type State = 'entering' | 'exiting';
type AnimationStates = UseAnimationConfig<State, HTMLDivElement>['animationStates'];
type AnimationCallback = UseAnimationConfig<State, HTMLDivElement>['onAnimationEnd'];
type Props = RenderableProps<TransitionProps> & {
  animationStates?: AnimationStates;
  initialAnimationStyles?: JSX.HTMLAttributes<HTMLDivElement>['style'];
};

const DEFAULT_ANIMATION_STATES = {};

/**
 * A intermediary component that handles animation for the messages component.
 *
 * The expected flow is as follows:
 * 1. message removed from the data
 * 2. onExiting called and a callback is passed which needs to be called to complete the transition
 * 3. state set to "exiting" in this component
 * 4. triggers useAnimation to perform the exit animation
 * 5. onAnimationEnd is called after the animation, which invokes the callback provided earlier
 * 6. onExited is called (done when the callback mentioned above is called)
 * 7. message is removed from the UI
 *
 * @param param0 Props of the message component
 */
export function MessageTransition({
  animationStates = DEFAULT_ANIMATION_STATES,
  initialAnimationStyles,
  onEntering,
  onExiting,
  ...transitionProps
}: Props) {
  const [state, setState] = useState<State>('entering');
  const animationCallbackRef = useRef<() => void>();
  const busyStateResolveRef = useRef<() => void>();
  const { controller, nodeRef } = useAnimation(state, {
    animationStates,
    isAnimatedOnMount: true,
    onAnimationEnd: useCallback<NonNullable<AnimationCallback>>(() => {
      // resolve animation callbacks & busyStates
      animationCallbackRef.current?.();
      animationCallbackRef.current = undefined;
      busyStateResolveRef.current?.();
      busyStateResolveRef.current = undefined;
    }, [])
  });
  const { addBusyState } = useMessagesContext();

  const onEnteringCallback = useCallback<NonNullable<TransitionProps['onEntering']>>(
    (node, callback, metadata) => {
      // cancel any existing animation
      if (animationCallbackRef.current) {
        animationCallbackRef.current = undefined;
        controller.cancel();
      }

      // resolve any existing busyState
      busyStateResolveRef.current?.();
      busyStateResolveRef.current = undefined;

      onEntering?.(node, undefined, metadata);
      // start the animation and set the callback & busyState
      setState('entering');

      // if there is no animation configured, resolve immediately
      if (
        animationStates['entering'] === undefined &&
        animationStates['exiting => entering'] === undefined
      ) {
        callback?.();
        return;
      }
      animationCallbackRef.current = callback;
      busyStateResolveRef.current = addBusyState?.('messages animating');
    },
    [animationStates, controller, addBusyState, onEntering]
  );

  const onExitingCallback = useCallback<NonNullable<TransitionProps['onExiting']>>(
    (node, callback, metadata) => {
      // cancel any existing animation
      if (animationCallbackRef.current) {
        animationCallbackRef.current = undefined;
        controller.cancel();
      }

      // resolve any existing busyState
      busyStateResolveRef.current?.();
      busyStateResolveRef.current = undefined;

      onExiting?.(node, undefined, metadata);
      // start the animation and set the callback & busyState
      setState('exiting');

      // if there is no animation configured, resolve immediately
      if (
        animationStates['exiting'] === undefined &&
        animationStates['entering => exiting'] === undefined
      ) {
        callback?.();
        return;
      }
      animationCallbackRef.current = callback;
      busyStateResolveRef.current = addBusyState?.('messages animating');
    },
    [animationStates, controller, addBusyState, onExiting]
  );

  // if animation is interrupted, the busyState may be left hanging. So clear that on unmount
  // Note: When using a class-based component inside a functional component, the timings of
  // componentDidMount and useEffect might differ. So, in order to guarantee the registration
  // of the cleanup function, use useLayoutEffect instead of useEffect. This way we can make sure
  // the cleanup registers at all times.
  useLayoutEffect(() => () => busyStateResolveRef.current?.(), []);

  return (
    <div ref={nodeRef} style={initialAnimationStyles}>
      <Transition
        {...transitionProps}
        onEntering={onEnteringCallback}
        onExiting={onExitingCallback}
      />
    </div>
  );
}
